import cx from 'classnames';
import { KeyboardEvent, ReactNode, RefObject, useMemo } from 'react';
import { PaginationState, Row, Table } from '@tanstack/react-table';
import { TableOptions } from '@/types/table';
import { TableProps } from '../Table';
import { ColumnOffsets, PositionedColumnDef } from '../utils/columnLayout';
import styles from '../Table.module.scss';
import TableToolbar from './TableToolbar';
import TablePaginator from './TablePaginator';
import AdjustTableDialog from './AdjustTableDialog/AdjustTableDialog';
import TableHead from './TableHead';
import TableRows from './TableRows';
import Scrollable from '../../ScrollArea/Scrollable';
import {
  getTableSearchHighlightContextValue,
  TableSearchHighlightContext,
} from '@/storybook/context/TableSearchHighlightContext';
import { getColumnConfigDialogProps } from './TableInternal.helpers';

const FocusDividers = ({
  hasHorizontalOverflow,
  stickyHorizontalOffsetStyle,
  stickyLeftOffsetStyle,
  stickyRightOffsetStyle,
}: {
  hasHorizontalOverflow: boolean;
  stickyHorizontalOffsetStyle: { left?: string; right?: string } | undefined;
  stickyLeftOffsetStyle: { left: string } | undefined;
  stickyRightOffsetStyle: { right: string } | undefined;
}) => {
  if (!hasHorizontalOverflow) {
    return null;
  }

  return (
    <>
      <div
        className={cx(styles.focusDivider, styles.focusDividerTop)}
        style={stickyHorizontalOffsetStyle}
        aria-hidden="true"
      />
      <div
        className={cx(styles.focusDivider, styles.focusDividerBottom)}
        style={stickyHorizontalOffsetStyle}
        aria-hidden="true"
      />
      <div
        className={cx(styles.focusDivider, styles.focusDividerLeft)}
        style={stickyLeftOffsetStyle}
        aria-hidden="true"
      />
      <div
        className={cx(styles.focusDivider, styles.focusDividerRight)}
        style={stickyRightOffsetStyle}
        aria-hidden="true"
      />
    </>
  );
};

export const TableToolbarSection = ({
  downloadButtonProps,
  showToolbarBorder,
  shouldShowAdjustTable,
  onAdjustTableClick,
  searchValue,
  onSearchChange,
  onSearchClear,
  searchPlaceholder,
  toolbarElement,
  headerElement,
  filterSlot,
  filterSlotWidth,
  additionalActionBarElement,
}: {
  downloadButtonProps: TableProps<unknown>['downloadButtonProps'];
  showToolbarBorder: boolean;
  shouldShowAdjustTable: boolean;
  onAdjustTableClick: () => void;
  searchValue: string | undefined;
  onSearchChange: ((value: string) => void) | undefined;
  onSearchClear: (() => void) | undefined;
  searchPlaceholder: string | undefined;
  toolbarElement: TableProps<unknown>['toolbarElement'];
  headerElement: TableProps<unknown>['headerElement'];
  filterSlot: TableProps<unknown>['filterSlot'];
  filterSlotWidth: TableProps<unknown>['filterSlotWidth'];
  additionalActionBarElement?: ReactNode;
}) => {
  return (
    <TableToolbar
      downloadButtonProps={downloadButtonProps}
      showToolbarBorder={showToolbarBorder}
      showAdjustTable={shouldShowAdjustTable}
      onAdjustTableClick={onAdjustTableClick}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      onSearchClear={onSearchClear}
      searchPlaceholder={searchPlaceholder}
      toolbarElement={toolbarElement}
      headerElement={headerElement}
      filterSlot={filterSlot}
      filterSlotWidth={filterSlotWidth}
      additionalActionBarElement={additionalActionBarElement}
    />
  );
};

export const TableFooterControls = <T,>({
  isLoading,
  options,
  table,
  pagination,
  totalCount,
  tableId,
  shouldShowAdjustTable,
  isAdjustTableOpen,
  handleAdjustTableClose,
  orderedAdjustTableColumns,
  orderedColumns,
  handleAdjustTableApply,
  handleAdjustTableReset,
  additionalHeaderLabels,
  columnConfigDialogProps,
}: {
  isLoading: boolean | undefined;
  options: TableOptions<T> | undefined;
  table: Table<T>;
  pagination: PaginationState;
  totalCount: number;
  tableId: string;
  shouldShowAdjustTable: boolean;
  isAdjustTableOpen: boolean;
  handleAdjustTableClose: () => void;
  orderedAdjustTableColumns: TableProps<T>['columns'] | undefined;
  orderedColumns: TableProps<T>['columns'];
  handleAdjustTableApply: (newOrder: string[], hasOrderChanged: boolean) => Promise<void>;
  handleAdjustTableReset: () => Promise<void>;
  additionalHeaderLabels: Record<string, string> | undefined;
  columnConfigDialogProps: ReturnType<typeof getColumnConfigDialogProps>;
}) => {
  return (
    <>
      <TablePaginator
        isLoading={isLoading}
        options={options}
        table={table}
        pagination={pagination}
        totalCount={totalCount}
        tableId={tableId}
      />
      {shouldShowAdjustTable && (
        <AdjustTableDialog
          isOpen={isAdjustTableOpen}
          onClose={handleAdjustTableClose}
          columns={orderedAdjustTableColumns ?? orderedColumns}
          onApply={handleAdjustTableApply}
          onResetToDefault={handleAdjustTableReset}
          additionalHeaderLabels={additionalHeaderLabels}
          columnConfig={columnConfigDialogProps}
        />
      )}
    </>
  );
};

export const TableScrollableSection = <T,>({
  showTopScrollbar,
  topScrollbarRef,
  topScrollbarInnerRef,
  tableWrapperRef,
  tableClasses,
  tableHeight,
  hasHorizontalOverflow,
  handleKeyDown,
  scrollableTableAreaLabel,
  hasGrouping,
  containsStickyColumns,
  dataTestId,
  table,
  options,
  positionedColumnDefs,
  columnOffsets,
  tableId,
  isLoading,
  onToggleRow,
  rowExpandedDelayed,
  visibleColumnCount,
  stickyHorizontalOffsetStyle,
  stickyLeftOffsetStyle,
  stickyRightOffsetStyle,
  searchHighlightValue,
}: {
  showTopScrollbar: boolean;
  topScrollbarRef: RefObject<HTMLDivElement>;
  topScrollbarInnerRef: RefObject<HTMLDivElement>;
  tableWrapperRef: RefObject<HTMLDivElement>;
  tableClasses: string;
  tableHeight: string;
  hasHorizontalOverflow: boolean;
  handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  scrollableTableAreaLabel: string;
  hasGrouping: boolean;
  containsStickyColumns: boolean;
  dataTestId: string;
  table: Table<T>;
  options: TableOptions<T> | undefined;
  positionedColumnDefs: PositionedColumnDef<T>[];
  columnOffsets: ColumnOffsets;
  tableId: string;
  isLoading: boolean;
  onToggleRow: (row: Row<T>) => void;
  rowExpandedDelayed: Record<string, boolean>;
  visibleColumnCount: number;
  stickyHorizontalOffsetStyle: { left?: string; right?: string } | undefined;
  stickyLeftOffsetStyle: { left: string } | undefined;
  stickyRightOffsetStyle: { right: string } | undefined;
  searchHighlightValue: string | undefined;
}) => {
  const tableSearchHighlightContextValue = useMemo(
    () => getTableSearchHighlightContextValue(searchHighlightValue),
    [searchHighlightValue],
  );

  return (
    <Scrollable>
      <div
        ref={topScrollbarRef}
        className={cx(styles.topScrollbar, { [styles.topScrollbarHidden]: !showTopScrollbar })}
        aria-hidden="true"
      >
        <div ref={topScrollbarInnerRef} className={styles.topScrollbarInner} />
      </div>
      <div className={styles.tableWrapper}>
        <div
          ref={tableWrapperRef}
          className={tableClasses}
          style={{ height: tableHeight }}
          tabIndex={hasHorizontalOverflow ? 0 : undefined}
          onKeyDown={hasHorizontalOverflow ? handleKeyDown : undefined}
          role={hasHorizontalOverflow ? 'region' : undefined}
          aria-label={hasHorizontalOverflow ? scrollableTableAreaLabel : undefined}
        >
          <TableSearchHighlightContext.Provider value={tableSearchHighlightContextValue}>
            <table
              className={cx({
                [styles.groupingActive]: hasGrouping,
                [styles.stickyTable]: containsStickyColumns,
              })}
              data-testid={dataTestId}
            >
              <TableHead
                table={table}
                options={options}
                positionedColumnDefs={positionedColumnDefs}
                columnOffsets={columnOffsets}
              />
              <TableRows
                tableId={tableId}
                table={table}
                isLoading={isLoading}
                onToggleRow={onToggleRow}
                rowExpandedDelayed={rowExpandedDelayed}
                options={options}
                columnCount={visibleColumnCount}
                positionedColumnDefs={positionedColumnDefs}
                columnOffsets={columnOffsets}
                searchHighlightValue={searchHighlightValue}
              />
            </table>
          </TableSearchHighlightContext.Provider>
        </div>
        <FocusDividers
          hasHorizontalOverflow={hasHorizontalOverflow}
          stickyHorizontalOffsetStyle={stickyHorizontalOffsetStyle}
          stickyLeftOffsetStyle={stickyLeftOffsetStyle}
          stickyRightOffsetStyle={stickyRightOffsetStyle}
        />
      </div>
    </Scrollable>
  );
};
